import React, { useState, useEffect, useCallback } from 'react'
import { X, Download, Eye, Calendar, Clock, CheckCircle, XCircle, AlertCircle, Loader, Image, Hash, Video } from 'lucide-react'
import videoExportService from '../services/videoExportService'
import imageExportService from '../services/imageExportService'
import { toUtcDate } from '../../utils/dateFormat'

/**
 * Past Samples Dialog Component
 * Displays past generated samples for a specific template_id from the queue
 */
const PastSamplesDialog = ({
  isOpen,
  onClose,
  templateId,
  templateName
}) => {
  const [samples, setSamples] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedSampleType, setSelectedSampleType] = useState('all')

  const fetchSamples = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      console.log('🔍 PastSamplesDialog: Fetching samples for templateId:', templateId, 'at', new Date().toISOString())
      
      if (!templateId) {
        throw new Error('Template ID is required')
      }

      const sampleType = selectedSampleType === 'all' ? null : selectedSampleType
      
      // Fetch both video and image queue jobs
      console.log('📡 Making API calls for video and image queue jobs...', { sampleType })
      const [videoResponse, imageResponse] = await Promise.all([
        videoExportService.getTemplateQueueJobs(templateId, sampleType, 100),
        imageExportService.getTemplateQueueJobs(templateId, sampleType, 100)
      ])

      const allSamples = []
      const seenIds = new Set() // Track seen IDs to prevent duplicates
      
      // Add video samples
      if (videoResponse.success && videoResponse.jobs) {
        videoResponse.jobs.forEach(job => {
          if (!seenIds.has(job.id)) {
            seenIds.add(job.id)
            allSamples.push({
              ...job,
              type: 'video',
              preview_url: job.video_url,
              final_url: job.video_url
            })
          }
        })
      }
      
      // Add image samples
      if (imageResponse.success && imageResponse.jobs) {
        imageResponse.jobs.forEach(job => {
          if (!seenIds.has(job.id)) {
            seenIds.add(job.id)
            allSamples.push({
              ...job,
              type: 'image',
              preview_url: job.image_url,
              final_url: job.image_url
            })
          }
        })
      }

      // Sort by creation date (newest first)
      allSamples.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      
      console.log('📊 Total samples found:', allSamples.length)
      console.log('📋 Video samples:', videoResponse.success ? videoResponse.jobs?.length || 0 : 0)
      console.log('📋 Image samples:', imageResponse.success ? imageResponse.jobs?.length || 0 : 0)
      
      setSamples(allSamples)
    } catch (err) {
      console.error('Error fetching samples:', err)
      setError('Failed to fetch samples: ' + err.message)
    } finally {
      setLoading(false)
    }
  }, [templateId, selectedSampleType])

  // Fetch samples when dialog opens, templateId changes, or selectedSampleType changes
  useEffect(() => {
    if (isOpen && templateId) {
      fetchSamples()
    }
  }, [isOpen, templateId, selectedSampleType, fetchSamples])

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />
      case 'queued':
      case 'picked':
      case 'preparing':
      case 'generating':
      case 'encoding':
      case 'finalizing':
        return <Loader className="w-4 h-4 text-blue-500 animate-spin" />
      default:
        return <AlertCircle className="w-4 h-4 text-yellow-500" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50'
      case 'failed':
        return 'text-red-600 bg-red-50'
      case 'queued':
      case 'picked':
      case 'preparing':
      case 'generating':
      case 'encoding':
      case 'finalizing':
        return 'text-blue-600 bg-blue-50'
      default:
        return 'text-yellow-600 bg-yellow-50'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return toUtcDate(dateString).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
  }

  const handleDownload = (sample) => {
    if (sample.final_url || sample.video_url || sample.image_url) {
      const url = sample.final_url || sample.video_url || sample.image_url
      const link = document.createElement('a')
      link.href = url
      link.download = sample.filename || `sample-${sample.id}.${sample.format || 'mp4'}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handlePreview = (sample) => {
    if (sample.final_url || sample.video_url || sample.image_url) {
      const url = sample.final_url || sample.video_url || sample.image_url
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  const uniqueSampleTypes = [...new Set(samples.map(s => s.sample_type).filter(Boolean))]

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Past Generated Samples</h2>
            <p className="text-sm text-gray-600 mt-1">
              Template: {templateName || 'Unknown Template'} (ID: {templateId || 'undefined'})
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Filter by type:</label>
            <select
              value={selectedSampleType}
              onChange={(e) => setSelectedSampleType(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              {uniqueSampleTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <button
              onClick={fetchSamples}
              className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex items-center space-x-2">
                <Loader className="w-5 h-5 animate-spin text-blue-500" />
                <span className="text-gray-600">Loading samples...</span>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600 font-medium">Error loading samples</p>
                <p className="text-gray-600 text-sm mt-1">{error}</p>
                <button
                  onClick={fetchSamples}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : samples.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">No samples found</p>
                <p className="text-gray-500 text-sm mt-1">
                  No generated samples found for this template
                </p>
              </div>
            </div>
          ) : (
            <div className="overflow-auto h-full">
              <div className="p-6">
                <div className="grid gap-4">
                  {samples.map((sample) => (
                    <div
                      key={sample.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      {/* Two-column layout: Preview on left, Info on right */}
                      <div className="flex gap-4">
                        {/* Left side: Full-height preview */}
                        <div className="flex-shrink-0">
                          {(sample.final_url || sample.video_url || sample.image_url) && (
                            <div className="w-32 h-40 bg-gray-100 rounded-lg border overflow-hidden">
                              <img
                                src={sample.final_url || sample.video_url || sample.image_url}
                                alt="Sample preview"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400 hidden">
                                {sample.type === 'image' ? <Image className="w-8 h-8" /> : <Video className="w-8 h-8" />}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Right side: All information */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-2">
                            {getStatusIcon(sample.status)}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(sample.status)}`}>
                              {sample.status.toUpperCase()}
                            </span>
                            <span className="text-sm text-gray-600">
                              {sample.sample_type || 'sample'}
                            </span>
                            <span className="text-sm text-gray-600">
                              {sample.type === 'image' ? <Image className="w-4 h-4 inline mr-1" /> : <Video className="w-4 h-4 inline mr-1" />}
                              {sample.format?.toUpperCase() || (sample.type === 'image' ? 'JPG' : 'MP4')}
                            </span>
                            {/* Queue ID Display */}
                            <div className="flex items-center gap-1 text-gray-400">
                              <Hash className="w-3 h-3" />
                              <span className="font-mono text-xs">{sample.id}</span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4" />
                              <span>Created: {formatDate(sample.created_at)}</span>
                            </div>
                            {sample.completed_at && (
                              <div className="flex items-center space-x-2">
                                <Clock className="w-4 h-4" />
                                <span>Completed: {formatDate(sample.completed_at)}</span>
                              </div>
                            )}
                          </div>

                          {sample.progress > 0 && sample.status !== 'completed' && (
                            <div className="mb-3">
                              <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                                <span>Progress</span>
                                <span>{sample.progress}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${sample.progress}%` }}
                                ></div>
                              </div>
                            </div>
                          )}

                          {sample.error_message && (
                            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                              <strong>Error:</strong> {sample.error_message}
                            </div>
                          )}

                          {/* Actions */}
                          {sample.status === 'completed' && (sample.final_url || sample.video_url || sample.image_url) && (
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handlePreview(sample)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Preview in new tab"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDownload(sample)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {samples.length} sample{samples.length !== 1 ? 's' : ''} found
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PastSamplesDialog
